export type EnumValue = string | number;

export interface EnumType<T extends EnumValue> {
  [key: string]: T;
}

export function enumKeys(type: EnumType<any>): Array<string> {
  if (typeof type !== 'object') return [];
  return Object.keys(type).filter(key => Number.isNaN(parseInt(key, 10)));
}

export function enumValues<T extends EnumValue>(type: EnumType<T | string>): Array<T> {
  return enumKeys(type).map(key => type[key] as any as T);
}

export function isEnumValue<T extends EnumValue>(value: any, type: EnumType<T | string>): value is T {
  return enumValues(type).includes(value);
}

/**
 * Type-happy way to call {@link #Array.includes}
 */
export function isAnyOf<T>(value: Nullish<T>, ...values: Array<T>): boolean {
  return isPresent(value) && values.includes(value);
}

export type Nullish<T> = T | null | undefined;

export function isPresent<T>(value: Nullish<T>): value is T {
  return value !== null && value !== undefined;
}

export function ifPresent<T, U>(value: Nullish<T>, action: (v: T) => U): U | undefined {
  if (isPresent(value)) return action(value);
}

export function present<T>(value: Nullish<T>, message = 'value not present'): T {
  if (isPresent(value)) {
    return value;
  } else {
    throw new Error(message);
  }
}

export function ensureString(value: any, message = 'value is not a string'): string {
  if (typeof value === 'string') {
    return value;
  } else {
    throw new Error(message);
  }
}

/**
 * NOTE there's a few more possibilities (obviously) but these don't have types at the moment and should be rare
 * cf https://developer.mozilla.org/en-US/docs/Glossary/Falsy
 */
export type Falsy = null | undefined | false | 0 | -0 | 0n;

export function isTruthy<T>(value: T | Falsy): value is T {
  return !!value;
}

export interface Lookup<Id, Entity> {
  (id: Nullish<Id>): Entity | null;
}

export function lookupFrom<Id, Entity extends { id: Id | null }>(list: Array<Entity>, entityDescription = 'entity'): Lookup<Id, Entity> {
  return id => {
    if (!isPresent(id)) return null;
    const entity = list.find(i => i.id === id);
    if (entity) return entity;
    console.warn(`no ${entityDescription} found with ID ${id}`); // eslint-disable-line
    return null;
  };
}

export function unsupported(): any {
  throw new Error('unsupported');
}

export type Constructor<T> = new (...args: Array<any>) => T;
